import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { ChevronDown } from "lucide-react";

import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  DialogPopover,
  DialogPopoverTrigger,
  DialogPopoverContent,
} from "../../components/ui/dialog-popover";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";

import { useToast } from "../../components/hooks/use-toast";
import useProject from "../../components/hooks/useProject";

import { where, documentId } from "firebase/firestore";
import firestoreService from "../../services/firestoreService";

export default function ProjectMembers() {
  const { projectId } = useParams();
  const { project } = useProject(projectId);

  const [projectMembers, setProjectMembers] = useState([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const { toast } = useToast();

  useEffect(() => {
    const fetchProjectMembers = async (project) => {
      try {
        const { owners = [], editors = [], viewers = [] } = project.roles;

        // Combine all user IDs from owners, editors, and viewers
        const allUserIds = [...owners, ...editors, ...viewers];

        // Split the user IDs into chunks of 30 or fewer
        const chunks = chunkArray(allUserIds, 30);
        const projectUsers = [];

        // Run Firestore queries for each chunk
        for (const chunk of chunks) {
          const users = await firestoreService.getWhere(
            "users",
            where(documentId(), "in", chunk)
          );

          // Map user data and determine their roles
          users.forEach((user) => {
            projectUsers.push({
              ...user,
              projectRole: getProjectRole(user.id, owners, editors, viewers),
            });
          });
        }

        setProjectMembers(projectUsers);
      } catch (error) {
        console.error("Error fetching project members.", error);

        toast({
          variant: "destructive",
          title: "Error fetching project members.",
          description: error.message,
        });
      } finally {
        setLoading(false);
      }
    };

    if (project) {
      fetchProjectMembers(project);
    }
  }, [toast, project]);

  // Helper function to split an array into chunks
  const chunkArray = (array, size) => {
    const chunked = [];

    for (let i = 0; i < array.length; i += size) {
      chunked.push(array.slice(i, i + size));
    }

    return chunked;
  };

  // Helper function to determine the user's project role
  const getProjectRole = (userId, owners, editors, viewers) => {
    if (owners.includes(userId)) return "owner";
    if (editors.includes(userId)) return "editor";
    if (viewers.includes(userId)) return "viewer";
    return null;
  };

  const handleProjectRoleChange = async (userId, projectRole) => {
    try {
      setProjectMembers((prevMembers) =>
        prevMembers.map((user) =>
          user.id === userId ? { ...user, projectRole: projectRole } : user
        )
      );

      setPopoverOpen(false);
    } catch (error) {
      console.error("Error updating user's project role.", error);

      toast({
        variant: "destructive",
        title: "Error updating user's project role.",
        description: error.message,
      });
    }
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Project Members</CardTitle>

        <CardDescription>
          Set the access of members of your project.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-6 max-h-[220px] min-h-[220px] overflow-y-auto mt-2">
        {/* Show some loading skeletons while loading */}
        {loading &&
          [...Array(3)].map((e, i) => (
            <div key={i} className="flex items-start justify-between space-x-4">
              <div className="flex items-center space-x-4">
                <Skeleton className="h-12 w-12 rounded-full" />

                <div className="space-y-1">
                  <Skeleton className="h-4 w-[100px]" />
                  <Skeleton className="h-4 w-[150px]" />
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <Skeleton className="h-8 mt-2 w-[90px]" />
              </div>
            </div>
          ))}

        {/* Project members */}
        {!loading &&
          projectMembers.map((user, index) => (
            <div
              key={index}
              className="flex align-top justify-between space-x-4"
            >
              <div className="flex items-center space-x-4">
                <Avatar className="h-12 w-12">
                  <AvatarFallback>
                    {user.firstName[0]}
                    {user.lastName[0]}
                  </AvatarFallback>
                </Avatar>

                <div>
                  <p className="text-sm font-medium leading-none">
                    {user.firstName} {user.lastName}
                  </p>

                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>

              <DialogPopover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <DialogPopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto capitalize"
                    disabled
                    // disabled={user.projectRole == "owner"}
                  >
                    {user.projectRole}
                    <ChevronDown className="ml-2 h-4 w-4 text-muted-foreground" />
                  </Button>
                </DialogPopoverTrigger>

                <DialogPopoverContent className="p-0" align="end">
                  <Command>
                    <CommandInput placeholder="Select new project role..." />

                    <CommandList>
                      <CommandEmpty>No project roles found.</CommandEmpty>

                      <CommandGroup>
                        <CommandItem
                          className="teamaspace-y-1 flex flex-col items-start px-4 py-2"
                          onSelect={() => {
                            handleProjectRoleChange(user.id, "viewer");
                          }}
                        >
                          <p>Viewer</p>

                          <p className="text-sm text-muted-foreground">
                            Can only view hazards.
                          </p>
                        </CommandItem>

                        <CommandItem
                          className="teamaspace-y-1 flex flex-col items-start px-4 py-2"
                          onSelect={() => {
                            handleProjectRoleChange(user.id, "editor");
                          }}
                        >
                          <p>Editor</p>

                          <p className="text-sm text-muted-foreground">
                            Can view and edit hazards.
                          </p>
                        </CommandItem>

                        <CommandItem
                          className="teamaspace-y-1 flex flex-col items-start px-4 py-2"
                          onSelect={() => {
                            handleProjectRoleChange(user.id, "owner");
                          }}
                        >
                          <p>Owner</p>
                          <p className="text-sm text-muted-foreground">
                            Can view, edit and delete hazards. Can edit and
                            delete project.
                          </p>
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </DialogPopoverContent>
              </DialogPopover>
            </div>
          ))}
      </CardContent>
    </Card>
  );
}
